import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Pencil, Download, X } from 'lucide-react';
import axios from 'axios';
import { PRODUCTS } from '../../../data/shopProducts';
import { BACKEND_URL, apiErr, SHOP_QUALITIES, SHOP_ACTION_TYPES, CATEGORY_OPTIONS, EMPTY_PRODUCT_FORM } from '../shared';

// ─────────────────────────────────────────────────────────────────────────────
// ShopTab
// ─────────────────────────────────────────────────────────────────────────────
export default function ShopTab({ showFeedback }) {
  const [products, setProducts]       = useState([]);
  const [loadingProds, setLoadingProds] = useState(true);
  const [showForm, setShowForm]         = useState(false);
  const [editingId, setEditingId]       = useState(null); // product.id string
  const [form, setForm]                 = useState(EMPTY_PRODUCT_FORM);
  const [saving, setSaving]             = useState(false);
  const [deletingId, setDeletingId]     = useState(null);
  const [seeding, setSeeding]           = useState(false);

  const fetchProducts = () => {
    setLoadingProds(true);
    axios.get(`${BACKEND_URL}/api/shop/products`, { withCredentials: true })
      .then(({ data }) => setProducts(data))
      .catch(() => showFeedback('err', 'Error al cargar productos'))
      .finally(() => setLoadingProds(false));
  };

  useEffect(() => { fetchProducts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSeed = async () => {
    if (!window.confirm(`¿Importar ${PRODUCTS.length} productos por defecto? Solo funciona si la tienda está vacía.`)) return;
    setSeeding(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/shop/seed`, PRODUCTS, { withCredentials: true });
      showFeedback('ok', `Importados ${data.inserted} productos correctamente.`);
      fetchProducts();
    } catch (err) {
      showFeedback('err', apiErr(err, 'Error al importar'));
    } finally {
      setSeeding(false);
    }
  };

  const handleEdit = (p) => {
    const opt = CATEGORY_OPTIONS.find(
      (o) => o.category === p.category && o.subcategory === p.subcategory && (o.subsubcategory || '') === (p.subsubcategory || '')
    );
    setEditingId(p.id);
    setForm({
      id:              p.id,
      name:            p.name,
      subtitle:        p.subtitle || '',
      icon:            p.icon || 'inv_misc_questionmark',
      quality:         p.quality || 'epic',
      badge:           p.badge || '',
      price_crystals:  p.price_crystals ?? 500,
      category:        p.category,
      subcategory:     p.subcategory,
      subsubcategory:  p.subsubcategory || '',
      action_type:     p.action_type || 'send_items',
      features_text:   (p.features || []).join('\n'),
      wow_item_ids:    (p.wow_item_ids || []).join(', '),
      hidden:          !!p.hidden,
      _catKey:         opt ? `${opt.category}|${opt.subcategory}|${opt.subsubcategory}` : '',
    });
    setShowForm(true);
  };

  const handleCancelForm = () => { setShowForm(false); setEditingId(null); setForm(EMPTY_PRODUCT_FORM); };

  const handleCatChange = (val) => {
    const [cat, sub, ss] = val.split('|');
    setForm((f) => ({ ...f, category: cat, subcategory: sub, subsubcategory: ss || '', _catKey: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      id:             form.id.trim(),
      name:           form.name.trim(),
      subtitle:       form.subtitle.trim(),
      icon:           form.icon.trim(),
      quality:        form.quality,
      badge:          form.badge.trim() || null,
      price_crystals: parseInt(form.price_crystals) || 0,
      category:       form.category,
      subcategory:    form.subcategory,
      subsubcategory: form.subsubcategory || null,
      action_type:    form.action_type,
      features:       form.features_text.split('\n').flatMap((s) => { const t = s.trim(); return t ? [t] : []; }),
      wow_item_ids:   form.wow_item_ids.split(',').flatMap((s) => { const n = parseInt(s.trim()); return isNaN(n) ? [] : [n]; }),
      hidden:         !!form.hidden,
    };
    try {
      if (editingId) {
        await axios.put(`${BACKEND_URL}/api/admin/shop/products/${editingId}`, payload, { withCredentials: true });
        showFeedback('ok', 'Producto actualizado.');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/shop/products`, payload, { withCredentials: true });
        showFeedback('ok', 'Producto creado.');
      }
      handleCancelForm();
      fetchProducts();
    } catch (err) {
      showFeedback('err', apiErr(err, 'Error al guardar'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('¿Eliminar este producto?')) return;
    setDeletingId(productId);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/shop/products/${productId}`, { withCredentials: true });
      showFeedback('ok', 'Producto eliminado.');
      setProducts((prev) => prev.filter((p) => p.id !== productId));
    } catch {
      showFeedback('err', 'Error al eliminar.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="admin-section anim-fade-up" style={{ animationDelay: '0.15s' }}>
      <div className="admin-section-header" style={{ flexWrap: 'wrap', gap: 8 }}>
        <h2 className="admin-section-title">Gestión de Tienda ({products.length} productos)</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {products.length === 0 && (
            <button className="admin-create-btn" onClick={handleSeed} disabled={seeding}
              style={{ background: 'rgba(255,140,0,0.15)', borderColor: 'rgba(255,140,0,0.5)', color: '#ffaa44' }}>
              <Download size={14} /> {seeding ? 'Importando...' : 'Importar catálogo por defecto'}
            </button>
          )}
          <button className="admin-create-btn" onClick={() => showForm ? handleCancelForm() : setShowForm(true)}>
            {showForm ? <><X size={14} /> Cancelar</> : <><Plus size={14} /> Nuevo Producto</>}
          </button>
        </div>
      </div>

      {/* Formulario en modal */}
      {showForm && (
        <div
          role="presentation"
          onClick={handleCancelForm}
          onKeyDown={() => {}}
          style={{
            position: 'fixed', inset: 0, zIndex: 1200,
            background: 'rgba(0, 10, 20, 0.78)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(900px, 100%)',
              maxHeight: 'calc(100dvh - 32px)',
              overflowY: 'auto',
              borderRadius: 14,
              border: '1px solid rgba(0, 229, 255, 0.28)',
              background: 'linear-gradient(180deg, rgba(0,30,55,0.97), rgba(0,18,34,0.97))',
              boxShadow: '0 20px 50px rgba(0, 229, 255, 0.12)',
            }}
          >
            <div
              style={{
                position: 'sticky', top: 0, zIndex: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 18px',
                background: 'linear-gradient(180deg, rgba(0,30,55,0.98), rgba(0,30,55,0.92))',
                borderBottom: '1px solid rgba(0, 229, 255, 0.18)',
              }}
            >
              <strong style={{ color: '#e0f7ff', letterSpacing: 0.5 }}>
                {editingId ? `✏️ Editando: ${editingId}` : '➕ Nuevo producto'}
              </strong>
              <button
                type="button"
                onClick={handleCancelForm}
                aria-label="Cerrar"
                style={{
                  border: '1px solid rgba(0, 229, 255, 0.25)',
                  background: 'rgba(0, 229, 255, 0.08)',
                  color: '#00e5ff',
                  borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700,
                }}
              >
                <X size={13} /> CERRAR
              </button>
            </div>
        <form onSubmit={handleSubmit} className="admin-form" style={{ padding: 18 }}>

          {/* Fila 1: ID + nombre */}
          <div className="admin-form-row">
            <div className="form-field" style={{ flex: 1 }}>
              <label htmlFor="ap-prod-id" className="form-label">ID (slug único)</label>
              <input id="ap-prod-id" className="form-input" style={{ paddingLeft: 14 }} placeholder="ej: espada_arthas_hc"
                value={form.id} onChange={(e) => setForm(prev => ({ ...prev, id: e.target.value }))}
                required disabled={!!editingId} />
            </div>
            <div className="form-field" style={{ flex: 2 }}>
              <label htmlFor="ap-prod-name" className="form-label">NOMBRE</label>
              <input id="ap-prod-name" className="form-input" style={{ paddingLeft: 14 }} placeholder="Nombre del producto"
                value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} required />
            </div>
          </div>

          {/* Fila 2: subtítulo + badge */}
          <div className="admin-form-row">
            <div className="form-field" style={{ flex: 3 }}>
              <label htmlFor="ap-prod-subtitle" className="form-label">SUBTÍTULO</label>
              <input id="ap-prod-subtitle" className="form-input" style={{ paddingLeft: 14 }} placeholder="Breve descripción"
                value={form.subtitle} onChange={(e) => setForm(prev => ({ ...prev, subtitle: e.target.value }))} />
            </div>
            <div className="form-field" style={{ flex: 1 }}>
              <label htmlFor="ap-prod-badge" className="form-label">BADGE</label>
              <input id="ap-prod-badge" className="form-input" style={{ paddingLeft: 14 }} placeholder="ej: ICC 25 HC"
                value={form.badge} onChange={(e) => setForm(prev => ({ ...prev, badge: e.target.value }))} />
            </div>
          </div>

          {/* Fila 3: icono + calidad + precio + acción */}
          <div className="admin-form-row">
            <div className="form-field" style={{ flex: 2 }}>
              <label htmlFor="ap-prod-icon" className="form-label">ICONO (slug zamimg)</label>
              <input id="ap-prod-icon" className="form-input" style={{ paddingLeft: 14 }} placeholder="inv_sword_01"
                value={form.icon} onChange={(e) => setForm(prev => ({ ...prev, icon: e.target.value }))} />
            </div>
            <div className="form-field" style={{ flex: 1 }}>
              <label htmlFor="ap-prod-quality" className="form-label">CALIDAD</label>
              <select id="ap-prod-quality" className="form-input form-select" style={{ paddingLeft: 14 }}
                value={form.quality} onChange={(e) => setForm(prev => ({ ...prev, quality: e.target.value }))}>
                {SHOP_QUALITIES.map((q) => <option key={q} value={q}>{q}</option>)}
              </select>
            </div>
            <div className="form-field" style={{ flex: 1 }}>
              <label htmlFor="ap-prod-price" className="form-label">PRECIO (Esc.)</label>
              <input id="ap-prod-price" type="number" min="0" className="form-input" style={{ paddingLeft: 14 }}
                value={form.price_crystals} onChange={(e) => setForm(prev => ({ ...prev, price_crystals: e.target.value }))} required />
            </div>
            <div className="form-field" style={{ flex: 1 }}>
              <label htmlFor="ap-prod-action" className="form-label">ACCIÓN</label>
              <select id="ap-prod-action" className="form-input form-select" style={{ paddingLeft: 14 }}
                value={form.action_type} onChange={(e) => setForm(prev => ({ ...prev, action_type: e.target.value }))}>
                {SHOP_ACTION_TYPES.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          </div>

          {/* Fila 4: categoría */}
          <div className="form-field">
            <label htmlFor="ap-prod-category" className="form-label">CATEGORÍA</label>
            <select id="ap-prod-category" className="form-input form-select" style={{ paddingLeft: 14 }}
              value={form._catKey || `${form.category}|${form.subcategory}|${form.subsubcategory}`}
              onChange={(e) => handleCatChange(e.target.value)}>
              {CATEGORY_OPTIONS.map((o) => (
                <option key={`${o.category}|${o.subcategory}|${o.subsubcategory}`}
                  value={`${o.category}|${o.subcategory}|${o.subsubcategory}`}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {/* Fila 5: características */}
          <div className="form-field">
            <label htmlFor="ap-prod-features" className="form-label">CARACTERÍSTICAS <span style={{ color: 'rgba(255,255,255,0.3)', fontWeight: 400 }}>(una por línea)</span></label>
            <textarea id="ap-prod-features" className="form-input admin-textarea" style={{ paddingLeft: 14, paddingTop: 10, resize: 'vertical' }}
              placeholder={"Nivel de objeto 277\nDrop Lich King 25HC\nEspecialidad: Físico"}
              value={form.features_text} onChange={(e) => setForm(prev => ({ ...prev, features_text: e.target.value }))} rows={4} />
          </div>

          {/* Fila 6: item IDs */}
          <div className="form-field">
            <label htmlFor="ap-prod-wow-ids" className="form-label">IDs DE ÍTEMS WoW <span style={{ color: 'rgba(255,255,255,0.3)', fontWeight: 400 }}>(separados por coma)</span></label>
            <input id="ap-prod-wow-ids" className="form-input" style={{ paddingLeft: 14 }} placeholder="51021, 51022"
              value={form.wow_item_ids} onChange={(e) => setForm(prev => ({ ...prev, wow_item_ids: e.target.value }))} />
          </div>

          {/* Fila 7: oculto (solo lo ven y compran los admin) */}
          <label htmlFor="ap-prod-hidden" style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#e0f7ff', fontSize: 13, cursor: 'pointer' }}>
            <input id="ap-prod-hidden" type="checkbox" checked={!!form.hidden}
              onChange={(e) => setForm(prev => ({ ...prev, hidden: e.target.checked }))} />
            Oculto <span style={{ color: 'rgba(255,255,255,0.4)' }}>(no sale en la tienda; solo los admin lo ven y lo pueden comprar para probarlo)</span>
          </label>

          <button type="submit" className="register-btn" disabled={saving}
            style={{ width: 'auto', padding: '10px 32px', alignSelf: 'flex-end' }}>
            {saving ? 'GUARDANDO...' : editingId ? 'GUARDAR CAMBIOS' : 'CREAR PRODUCTO'}
          </button>
        </form>
          </div>
        </div>
      )}

      {/* Lista de productos */}
      {loadingProds ? (
        <div className="armory-empty">Cargando productos...</div>
      ) : products.length === 0 ? (
        <div className="armory-empty">No hay productos. Importa el catálogo por defecto o crea uno.</div>
      ) : (
        <div className="admin-news-list">
          {products.map((p) => (
            <div key={p.id} className="admin-news-item">
              <div className="admin-news-item-left" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <img
                  src={`https://wow.zamimg.com/images/wow/icons/large/${p.icon}.jpg`}
                  alt={p.name}
                  style={{ width: 32, height: 32, borderRadius: 6, flexShrink: 0 }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <div>
                  <p className="admin-news-item-title" style={{ marginBottom: 2 }}>{p.name}</p>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span className="news-category-badge"
                      style={{ background: 'rgba(0,180,255,0.12)', border: '1px solid rgba(0,180,255,0.3)', color: '#69CCF0' }}>
                      {p.subcategory}
                    </span>
                    <span style={{ color: '#00e5ff', fontSize: 12, fontWeight: 700 }}>
                      {p.price_crystals?.toLocaleString()} ❄
                    </span>
                    {p.hidden && (
                      <span className="news-category-badge"
                        style={{ background: 'rgba(255,140,0,0.12)', border: '1px solid rgba(255,140,0,0.4)', color: '#ffaa44' }}>
                        OCULTO
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="admin-edit-btn" onClick={() => handleEdit(p)} title="Editar">
                  <Pencil size={14} />
                </button>
                <button className="admin-delete-btn" onClick={() => handleDelete(p.id)}
                  disabled={deletingId === p.id} title="Eliminar">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
